import GuideLayout from "../components/GuideLayout";
import { IMAGES } from "../data/content";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Private wine tour",
  name: "Private Tuscany Wine Tours",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["Chianti", "San Gimignano", "Montalcino", "Lucca", "Pisa", "Florence", "Siena", "Tuscany"],
  description:
    "Private, chauffeur-driven wine tours through the Chianti region, San Gimignano and beyond, departing from Lucca, Pisa and Florence in a Mercedes V-Class.",
  url: "https://yourblacklimo.com/tuscany-wine-tours",
};

const CONTENT = {
  en: {
    seoTitle: "Private Tuscany Wine Tours | Chianti Wine Tasting from Lucca & Pisa — Your Black Limo",
    seoDesc:
      "Private chauffeur-driven wine tours through Chianti, San Gimignano and Montalcino from Lucca, Pisa and Florence. Mercedes V-Class, fully bespoke — no groups, no coaches.",
    overline: "Tuscany Wine Tours",
    h1: "Private Wine Tours through Chianti and Beyond",
    intro:
      "A day among the vines, poured at the source. We drive you door-to-door through the rolling hills of Chianti, San Gimignano and the great appellations of southern Tuscany — so you can taste freely, linger over lunch and leave the driving to us.",
    heroAlt: "Chianti vineyards on the rolling hills of Tuscany",
    blocks: [
      {
        title: "A day in the vines",
        body: "We collect you in the late morning and wind up into the hills, stopping wherever the view demands it. A first tasting at a family estate, a long Tuscan lunch among the vines, then a second cellar in the afternoon — perhaps a hilltop village and an aperitivo before the golden drive home. Every itinerary is shaped around your taste, your pace and the season.",
      },
      {
        title: "What's included",
        body: "A private Mercedes V-Class with a professional, English-speaking chauffeur; door-to-door pickup from your hotel, villa, port or airport; and a relaxed, fully bespoke pace — never a fixed group or coach. Winery visits, tastings, meals and any entrance fees are booked and paid directly by the client.",
      },
    ],
    cards: {
      overline: "The Regions",
      title: "The vineyards we know best",
      items: [
        { title: "Chianti Classico", body: "The heartland between Florence and Siena — Sangiovese vineyards, stone farmhouses and the black-rooster wineries that made Tuscany famous." },
        { title: "San Gimignano", body: "The town of fourteen towers and the crisp white Vernaccia — the only white wine in Tuscany with its own historic denomination." },
        { title: "Montalcino & Montepulciano", body: "For the serious oenophile: the deep, age-worthy Brunello di Montalcino and the elegant Vino Nobile di Montepulciano." },
      ],
    },
    cta: {
      title: "Design your private wine day in Tuscany",
      secondaryLabel: "Explore itineraries",
      secondaryHref: "/cruise-tours",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "Where do the wine tours depart from?", a: "We collect you door-to-door from anywhere in Tuscany — your hotel, villa, a cruise port or an airport in Pisa, Lucca or Florence." },
        { q: "How many wineries can we visit in a day?", a: "Typically two, with a relaxed Tuscan lunch in between. We keep the pace unhurried so you can truly enjoy each stop." },
      ],
    },
  },
  it: {
    seoTitle: "Tour del Vino in Toscana | Degustazione nel Chianti da Lucca e Pisa — Your Black Limo",
    seoDesc:
      "Tour del vino privati con autista tra Chianti, San Gimignano e Montalcino da Lucca, Pisa e Firenze. Mercedes Classe V, tutto su misura — niente gruppi, niente pullman.",
    overline: "Tour del Vino in Toscana",
    h1: "Tour del Vino Privati nel Chianti e Oltre",
    intro:
      "Una giornata tra i vigneti, con il vino servito alla fonte. Ti accompagniamo porta a porta tra le colline del Chianti, San Gimignano e le grandi denominazioni della Toscana meridionale — così puoi degustare in libertà, indugiare a pranzo e lasciare a noi la guida.",
    heroAlt: "Vigneti del Chianti sulle colline della Toscana",
    blocks: [
      {
        title: "Una giornata tra i vigneti",
        body: "Ti veniamo a prendere a metà mattina e saliamo tra le colline, fermandoci ovunque il panorama lo richieda. Una prima degustazione in una cantina di famiglia, un lungo pranzo toscano tra le vigne, poi una seconda cantina nel pomeriggio — magari un borgo in cima al colle e un aperitivo prima del ritorno nella luce dorata. Ogni itinerario è modellato sul tuo gusto, il tuo ritmo e la stagione.",
      },
      {
        title: "Cosa è incluso",
        body: "Una Mercedes Classe V privata con autista professionista che parla inglese; prelievo porta a porta dal tuo hotel, villa, porto o aeroporto; e un ritmo rilassato e completamente su misura — mai un gruppo fisso o un pullman. Visite alle cantine, degustazioni, pasti ed eventuali biglietti d'ingresso sono prenotati e pagati direttamente dal cliente.",
      },
    ],
    cards: {
      overline: "Le Regioni",
      title: "I vigneti che conosciamo meglio",
      items: [
        { title: "Chianti Classico", body: "Il cuore tra Firenze e Siena — vigneti di Sangiovese, casali in pietra e le cantine del gallo nero che hanno reso famosa la Toscana." },
        { title: "San Gimignano", body: "La città dalle quattordici torri e la fresca Vernaccia — l'unico vino bianco della Toscana con una propria denominazione storica." },
        { title: "Montalcino e Montepulciano", body: "Per l'appassionato più esigente: il profondo e longevo Brunello di Montalcino e l'elegante Vino Nobile di Montepulciano." },
      ],
    },
    cta: {
      title: "Progetta la tua giornata del vino in Toscana",
      secondaryLabel: "Esplora gli itinerari",
      secondaryHref: "/cruise-tours",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "Da dove partono i tour del vino?", a: "Ti veniamo a prendere porta a porta ovunque in Toscana — hotel, villa, porto crociere o aeroporto di Pisa, Lucca o Firenze." },
        { q: "Quante cantine possiamo visitare in un giorno?", a: "Di norma due, con un rilassato pranzo toscano nel mezzo. Manteniamo un ritmo tranquillo per goderti davvero ogni tappa." },
      ],
    },
  },
};

export default function WineTourGuide() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="wine-tour-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/tuscany-wine-tours", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={"https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/62f11f6849acfe82a8925e5cb1feeea311cd254f22f967d0015036c4d946bf7e.jpeg"}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      cta={c.cta}
    />
  );
}
